import type { StorageLike } from "../lib/sessionHandoffStorage.ts";

const LAST_ALIAS_KEY = "comodo.participant.lastAlias.v1";
const DRAFT_PREFIX = "comodo.participant.drafts.v1";
const MAX_REPLY_DRAFTS = 50;

export interface ParticipantDraftState {
  post: string;
  replies: Record<string, string>;
}

export function readLastParticipantAlias(
  storage: StorageLike | null = browserLocalStorage(),
): string | null {
  const alias = safeGet(storage, LAST_ALIAS_KEY)?.trim() ?? "";
  return alias || null;
}

export function writeLastParticipantAlias(
  alias: string,
  storage: StorageLike | null = browserLocalStorage(),
): void {
  const normalized = alias.trim();
  if (normalized) safeSet(storage, LAST_ALIAS_KEY, normalized);
}

export function readParticipantDrafts(
  sessionId: string,
  storage: StorageLike | null = browserSessionStorage(),
): ParticipantDraftState {
  const raw = safeGet(storage, draftKey(sessionId));
  if (!raw) return emptyDrafts();
  try {
    const parsed = JSON.parse(raw) as { post?: unknown; replies?: unknown };
    const post = typeof parsed.post === "string" ? parsed.post : "";
    const replies: Record<string, string> = {};
    if (isRecord(parsed.replies)) {
      for (const [replyId, value] of Object.entries(parsed.replies).slice(0, MAX_REPLY_DRAFTS)) {
        if (typeof value === "string" && value) replies[replyId] = value;
      }
    }
    return { post, replies };
  } catch {
    return emptyDrafts();
  }
}

export function writeParticipantPostDraft(
  sessionId: string,
  post: string,
  storage: StorageLike | null = browserSessionStorage(),
): void {
  writeDrafts(sessionId, { ...readParticipantDrafts(sessionId, storage), post }, storage);
}

export function writeParticipantReplyDraft(
  sessionId: string,
  replyId: string,
  body: string,
  storage: StorageLike | null = browserSessionStorage(),
): void {
  const current = readParticipantDrafts(sessionId, storage);
  const replies = { ...current.replies };
  if (body) replies[replyId] = body;
  else delete replies[replyId];
  writeDrafts(sessionId, { ...current, replies }, storage);
}

function writeDrafts(
  sessionId: string,
  drafts: ParticipantDraftState,
  storage: StorageLike | null,
): void {
  const replies = Object.fromEntries(
    Object.entries(drafts.replies).filter(([, value]) => value).slice(0, MAX_REPLY_DRAFTS),
  );
  if (!drafts.post && Object.keys(replies).length === 0) {
    safeRemove(storage, draftKey(sessionId));
    return;
  }
  safeSet(storage, draftKey(sessionId), JSON.stringify({ post: drafts.post, replies }));
}

function draftKey(sessionId: string): string {
  return `${DRAFT_PREFIX}.${sessionId}`;
}

function browserLocalStorage(): StorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function browserSessionStorage(): StorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function safeGet(storage: StorageLike | null, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(storage: StorageLike | null, key: string, value: string): void {
  try {
    storage?.setItem(key, value);
  } catch {
    // Draft persistence must never block participant input.
  }
}

function safeRemove(storage: StorageLike | null, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    // Draft persistence must never block participant input.
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function emptyDrafts(): ParticipantDraftState {
  return { post: "", replies: {} };
}
